import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useClipboard,
} from "@chakra-ui/react"
import { useEffect, useState } from "react"
import { useProvider } from "wagmi"

import Link from "@/common/components/Link"
import { EXTERNAL_ROUTES } from "@/common/constants/routes"
import Check from "@/ui/components/icons/Check"
import { AddressCard } from "@/ui/components/icons/font-awesome/AddressCard"
import { ArrowUpRight } from "@/ui/components/icons/font-awesome/ArrowUpRight"
import { Copy } from "@/ui/components/icons/font-awesome/Copy"
import { LinkSimple } from "@/ui/components/icons/font-awesome/LinkSimple"
import { shortenIfAddress } from "@/web3/helpers/address"
import { useMultichainAccount } from "@/web3/hooks/useMultichainAccount"

type Steps = "warning" | "signatureCollection"

type SmartContractWalletAuthenticationWarningProps = {
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  onCancel: () => void
  onContinue: () => void
  step: Steps
  safeTransactionUrl: string | null
  signatureError: string | null
}

const ModalWrapper = ({
  children,
  isOpen,
  onClose,
}: {
  children: React.ReactNode
  isOpen: boolean
  onClose: () => void
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent maxWidth={408} top={{ base: "unset", lg: "60px" }}>
        {children}
      </ModalContent>
    </Modal>
  )
}

const SmartContractWalletAuthenticationWarning = ({
  isOpen,
  isLoading,
  onClose,
  onCancel,
  onContinue,
  step,
  safeTransactionUrl,
  signatureError,
}: SmartContractWalletAuthenticationWarningProps) => {
  const provider = useProvider()

  const { multichainAccount } = useMultichainAccount()
  const { address } = multichainAccount

  const [chainReference, setChainReference] = useState<number | null>(null)

  useEffect(() => {
    if (!provider) return

    const getNetwork = async () => {
      const network = await provider.getNetwork()

      setChainReference(network.chainId)
    }

    getNetwork()
  }, [provider])

  const { onCopy, hasCopied } = useClipboard(safeTransactionUrl ?? "")

  const ConnectedAddress = () => {
    return (
      <HStack justifyContent="space-between">
        <HStack>
          <Icon as={AddressCard} fill="gray.500" />
          <Text>Connected</Text>
        </HStack>
        <Link
          isExternal
          href={
            chainReference
              ? EXTERNAL_ROUTES.etherscan.address(chainReference, address)
              : ""
          }
        >
          <HStack>
            <Text>{shortenIfAddress(address)}</Text>
            <Icon as={ArrowUpRight} color="gray.500" />
          </HStack>
        </Link>
      </HStack>
    )
  }

  const TransactionUrl = () => {
    if (!safeTransactionUrl) return null

    return (
      <HStack justifyContent="space-between">
        <HStack>
          <Icon as={LinkSimple} color="gray.500" fill="gray.500" />
          <Text>Transaction url</Text>
        </HStack>
        <Link isExternal href={safeTransactionUrl}>
          <HStack>
            <Text>{safeTransactionUrl?.slice(8, 25)}...</Text>
            <Icon as={ArrowUpRight} color="gray.500" />
          </HStack>
        </Link>
      </HStack>
    )
  }

  const AlertBox = () => {
    return (
      <Alert alignItems="flex-start" status="warning">
        <AlertIcon />
        <Stack>
          <AlertTitle>Do not close this window</AlertTitle>
          <AlertDescription>
            Closing this window before collecting all signatures will cancel
            authentication and you will need to start over.
          </AlertDescription>
        </Stack>
      </Alert>
    )
  }

  if (step === "signatureCollection") {
    return (
      <ModalWrapper isOpen={isOpen} onClose={onClose}>
        <ModalHeader
          borderBottomColor="gray.100"
          borderBottomWidth={1}
          p={4}
          textStyle="h5"
        >
          Waiting for signatures
        </ModalHeader>
        <ModalBody p={4}>
          <Stack gap={8}>
            <Stack gap={1}>
              <ConnectedAddress />
              <TransactionUrl />
            </Stack>
            <Flex justifyContent="center">
              <Button
                isDisabled={!safeTransactionUrl}
                variant="outline"
                width="50%"
                onClick={onCopy}
              >
                <HStack>
                  <Text>Copy transaction url</Text>
                  <Icon as={hasCopied ? Check : Copy} fill="gray.500" />
                </HStack>
              </Button>
            </Flex>
            <Text color="gray.700" textAlign="center" textStyle="body-sm">
              Share this transaction url with the other signers to complete your
              sign-in to Tally.
            </Text>
            <AlertBox />
            {signatureError && (
              <Alert alignItems="flex-start" status="error">
                <AlertIcon />
                <Stack>
                  <AlertTitle>Invalid signature</AlertTitle>
                  <AlertDescription>
                    An error occurred while signing the message. Signing
                    messages via the Safe IOS app is not supported, please use
                    desktop instead.
                  </AlertDescription>
                </Stack>
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={4} p={4}>
          <Button variant="outline" width="full" onClick={onCancel}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalWrapper>
    )
  }

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <ModalHeader
        borderBottomColor="gray.100"
        borderBottomWidth={1}
        p={4}
        textStyle="h5"
      >
        Sign in with Safe
      </ModalHeader>
      <ModalBody p={4}>
        <Stack gap={8}>
          <Text>
            We&apos;ve detected that you&apos;re using a Safe wallet. Please
            sign the message in your Safe app once you click <b>Continue</b>.
          </Text>
          <Text>
            Remember, <b>multiple signatures may be required</b> to reach the
            approval threshold.
          </Text>
          <ConnectedAddress />
          <AlertBox />
        </Stack>
      </ModalBody>
      <ModalFooter gap={4} p={4}>
        <Button
          isDisabled={isLoading}
          variant="outline"
          width="full"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          isDisabled={isLoading}
          variant="primary"
          width="full"
          onClick={onContinue}
        >
          Continue
        </Button>
      </ModalFooter>
    </ModalWrapper>
  )
}

export default SmartContractWalletAuthenticationWarning
